import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Check, X, FileText, RefreshCw } from "lucide-react";

export default function COAReview() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({}); // {coa_id: true}

  const load = () => {
    setLoading(true);
    api.get("/admin/coas/review-queue").then((r) => { setQueue(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const decide = async (coa, decision) => {
    let notes = "";
    if (decision === "reject") {
      notes = window.prompt("Reason for rejection (sent to the submitter):", "");
      if (notes === null) return; // cancelled
    }
    setActing({ ...acting, [coa.id]: true });
    try {
      await api.post(`/admin/coas/${coa.id}/${decision}`, { notes, reason: notes });
      toast.success(decision === "approve" ? "Published to COA Hub" : "Submission rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setActing((s) => ({ ...s, [coa.id]: false })); }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-10" data-testid="admin-coa-review">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Review Queue</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">COA submissions awaiting review</h1>
          <p className="text-sm text-[#5E636E] mt-1">User-submitted lab reports. Approve to publish to the public COA Hub, or reject with a reason (emailed to the submitter).</p>
        </div>
        <button onClick={load} className="btn-secondary inline-flex items-center gap-2" data-testid="refresh-queue-btn">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="mt-8 border border-[#D1D5DB] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0B1F] text-white">
            <tr>
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">Submitted</th>
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">SKU · Batch</th>
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">Lab · Date</th>
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">Purity</th>
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">By</th>
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">PDF</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#5E636E]">Loading…</td></tr>}
            {!loading && queue.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-[#5E636E]">Inbox zero. No COAs awaiting review.</td></tr>}
            {queue.map((c) => (
              <tr key={c.id} className="border-t border-[#E7E8EA] hover:bg-[#F7F7F8] align-top" data-testid={`review-row-${c.id}`}>
                <td className="px-4 py-3 font-mono text-[11px] text-[#5E636E]">{(c.submitted_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="font-mono text-[12px] font-semibold">{c.product_sku}</div>
                  <div className="font-mono text-[11px] text-[#5E636E]">{c.batch_number}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-[13px]">{c.testing_lab}</div>
                  <div className="font-mono text-[11px] text-[#5E636E]">{c.test_date}</div>
                </td>
                <td className="px-4 py-3 font-mono text-[12px]">{c.purity_percent || "—"}%</td>
                <td className="px-4 py-3 text-[12px]">{c.submitted_by}</td>
                <td className="px-4 py-3">
                  {c.coa_pdf_url ? (
                    <a href={c.coa_pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] text-[#003366] hover:underline" data-testid={`view-pdf-${c.id}`}>
                      <FileText className="h-3.5 w-3.5" /> View
                    </a>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => decide(c, "approve")} disabled={acting[c.id]} className="inline-flex items-center gap-1 text-[12px] text-[#0B6623] hover:underline mr-3 disabled:opacity-50" data-testid={`approve-${c.id}`}>
                    <Check className="h-3.5 w-3.5" /> Approve
                  </button>
                  <button onClick={() => decide(c, "reject")} disabled={acting[c.id]} className="inline-flex items-center gap-1 text-[12px] text-[#A30414] hover:underline disabled:opacity-50" data-testid={`reject-${c.id}`}>
                    <X className="h-3.5 w-3.5" /> Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
